import { ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import {
  amountDivideDecimals,
  findDecimalByChainNameAndAsset,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";

import { TableColumnKey, TableRow } from "../types/Table";
import { useChains } from "src/components/FetchConfig";
import { GetListRes, WalletBalanceMonitorDetail } from "../types/GetListRes";
import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTransformRowData = ({
  apiList,
}: {
  apiList: ApiResult<GetListRes>;
}) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te } = useTranslation(translatePrefix);
  const { selectChainByPieces } = useChains();

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const getChainName = () => {
        const chainInfo = selectChainByPieces({
          chain_type: item.chain_type,
          chain_id: item.chain_id,
        });

        return chainInfo?.name || "";
      };

      const getThresholdValue = (
        detail: WalletBalanceMonitorDetail | undefined,
      ) => {
        if (!detail) {
          return undefined;
        }

        const decimal = findDecimalByChainNameAndAsset(
          String(detail.chain_type),
          detail.chain_id,
          detail.asset_name,
        );

        return amountDivideDecimals(detail.threshold_value, Number(decimal));
      };

      const getThreshold = () => {
        let lowerThreshold: string | undefined = undefined;
        let upperThreshold: string | undefined = undefined;

        if (!item.details) {
          return { lowerThreshold, upperThreshold };
        }

        const lowerThresholdDetail = item.details.find(
          (detail) => detail.criteria === EnumCriteria.LessThan,
        );
        const upperThresholdDetail = item.details.find(
          (detail) => detail.criteria === EnumCriteria.GreaterThan,
        );

        lowerThreshold = getThresholdValue(lowerThresholdDetail);
        upperThreshold = getThresholdValue(upperThresholdDetail);

        return { lowerThreshold, upperThreshold };
      };

      const { lowerThreshold, upperThreshold } = getThreshold();

      const mappedResult = [
        [TableColumnKey.ChainName, t(TableColumnKey.ChainName), getChainName()],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [
          TableColumnKey.WalletAddress,
          t(TableColumnKey.WalletAddress),
          item.wallet_address,
        ],
        [
          TableColumnKey.ThresholdLower,
          t(TableColumnKey.ThresholdLower),
          lowerThreshold,
        ],
        [
          TableColumnKey.ThresholdUpper,
          t(TableColumnKey.ThresholdUpper),
          upperThreshold,
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item?.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item?.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTransformRowData;
